import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import posthog from 'posthog-js';
import { authApi } from '@/lib/api';
import type { User, Restaurant, RestaurantRole } from '@/lib/api';

interface AuthState {
  user: User | null;
  restaurants: Restaurant[];
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  currentRestaurantId: number | null;
  currentRole: RestaurantRole | null;

  impersonating: { userId: number; userName: string } | null;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  register: (data: {
    name: string;
    email: string;
    password: string;
    password_confirmation: string;
    phone?: string;
    restaurant_name?: string;
    partner_code?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
    referrer?: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  setLoading: (loading: boolean) => void;
  setCurrentRestaurant: (restaurantId: number) => void;
  setCurrentRole: (role: RestaurantRole | null) => void;
  getCurrentRestaurantId: () => number | null;
  startImpersonation: (userId: number, userName: string, userToken: string) => Promise<void>;
  exitImpersonation: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      restaurants: [],
      token: localStorage.getItem('token'),
      isLoading: true,
      isAuthenticated: false,
      currentRestaurantId: null,
      currentRole: null,
      // Restore impersonation state on page refresh
      impersonating: (() => {
        try {
          const raw = localStorage.getItem('impersonation_meta');
          return raw ? JSON.parse(raw) : null;
        } catch {
          return null;
        }
      })(),

      setLoading: (loading) => set({ isLoading: loading }),

      setCurrentRestaurant: (restaurantId) => {
        const state = get();
        const restaurant = state.restaurants.find(r => r.id === restaurantId);
        set({
          currentRestaurantId: restaurantId,
          currentRole: restaurant?.role || null,
        });
      },

      setCurrentRole: (role) => {
        set({ currentRole: role });
      },

      getCurrentRestaurantId: () => {
        const state = get();
        // If currentRestaurantId is set, use it
        if (state.currentRestaurantId) {
          // Verify the restaurant still exists in the user's list
          const exists = state.restaurants.some(r => r.id === state.currentRestaurantId);
          if (exists) return state.currentRestaurantId;
        }
        // Fall back to first restaurant
        return state.restaurants[0]?.id || null;
      },

      login: async (email, password) => {
        set({ isLoading: true });
        try {
          const response = await authApi.login({ email, password });
          const { user, token } = response.data;

          localStorage.setItem('token', token);

          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
          });

          // Fetch full user data with restaurants
          await get().checkAuth();
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      register: async (data) => {
        set({ isLoading: true });
        try {
          const response = await authApi.register(data);
          const { user, restaurant, token } = response.data;

          localStorage.setItem('token', token);

          set({
            user,
            restaurants: restaurant ? [restaurant] : [],
            token,
            isAuthenticated: true,
            isLoading: false,
            currentRestaurantId: restaurant?.id ?? null,
            currentRole: restaurant ? 'owner' : null,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        try {
          await authApi.logout();
        } catch {
          // Ignore errors on logout
        } finally {
          posthog.reset();
          localStorage.removeItem('token');
          set({
            user: null,
            restaurants: [],
            token: null,
            isAuthenticated: false,
            currentRestaurantId: null,
            currentRole: null,
          });
        }
      },

      startImpersonation: async (userId, userName, userToken) => {
        const adminToken = localStorage.getItem('token');
        if (adminToken) {
          localStorage.setItem('impersonation_admin_token', adminToken);
        }
        const meta = { userId, userName };
        localStorage.setItem('impersonation_meta', JSON.stringify(meta));
        localStorage.setItem('token', userToken);
        set({ impersonating: meta });
        await get().checkAuth();
      },

      exitImpersonation: async () => {
        const adminToken = localStorage.getItem('impersonation_admin_token');
        if (!adminToken) return;

        localStorage.setItem('token', adminToken);
        localStorage.removeItem('impersonation_admin_token');
        localStorage.removeItem('impersonation_meta');
        set({ impersonating: null });
        await get().checkAuth();
      },

      checkAuth: async () => {
        const token = localStorage.getItem('token');

        if (!token) {
          set({ isLoading: false, isAuthenticated: false });
          return;
        }

        try {
          const response = await authApi.getUser();
          const { user, restaurants } = response.data;

          const state = get();
          let currentRestaurantId = state.currentRestaurantId;

          // If no current restaurant or it doesn't exist in user's restaurants, select first one
          if (!currentRestaurantId || !restaurants.some(r => r.id === currentRestaurantId)) {
            currentRestaurantId = restaurants[0]?.id || null;
          }

          // Get the user's role for the current restaurant
          const currentRestaurant = restaurants.find(r => r.id === currentRestaurantId);
          const currentRole = currentRestaurant?.role || null;

          posthog.identify(String(user.id), {
            email: user.email,
            name: user.name,
          });

          set({
            user,
            restaurants,
            token,
            isAuthenticated: true,
            isLoading: false,
            currentRestaurantId,
            currentRole,
          });
        } catch {
          localStorage.removeItem('token');
          set({
            user: null,
            restaurants: [],
            token: null,
            isAuthenticated: false,
            isLoading: false,
            currentRestaurantId: null,
            currentRole: null,
          });
        }
      },
    }),
    {
      name: 'pleko-auth',
      partialize: (state) => ({
        currentRestaurantId: state.currentRestaurantId,
      }),
    }
  )
);

// Selector hooks for convenience
export const useCurrentRestaurantId = () => {
  const { currentRestaurantId, restaurants } = useAuthStore();
  if (currentRestaurantId) {
    const exists = restaurants.some(r => r.id === currentRestaurantId);
    if (exists) return currentRestaurantId;
  }
  return restaurants[0]?.id || null;
};

export const useCurrentRole = () => useAuthStore((state) => state.currentRole);

export const useIsOwner = () => useAuthStore((state) => state.currentRole === 'owner');

export const useCanEdit = () => {
  const role = useAuthStore((state) => state.currentRole);
  return role && ['owner', 'admin', 'chef'].includes(role);
};

export const useCanManageTeam = () => {
  const role = useAuthStore((state) => state.currentRole);
  return role && ['owner', 'admin'].includes(role);
};

export const useCanManageBilling = () => {
  const role = useAuthStore((state) => state.currentRole);
  return role === 'owner';
};
